import * as React from 'react';
import { useRouter } from 'next/router';

import cx from 'classnames';
import { trackEvent } from 'integrations/metrics';
import { EventNames } from 'integrations/metrics/events';

import { useFeatureFlag } from '@glossgenius/core-features-react-sdk';

import { Button } from 'glossDS/Button/Button';

import { FEATURE_FLAGS } from 'core-features-config/activeFeatureFlags';

import { useBusiness } from 'hooks/business/useBusiness';
import { useConfig } from 'hooks/config/useConfig';
import { useAppContext } from 'hooks/context/useAppContext';
import { useFloatingNav } from 'hooks/navigation/useFloatingNav';
import { useFocusTrap } from 'hooks/useFocusTrap/useFocusTrap';
import { useWebsite } from 'hooks/website/useWebsite';

import { BagCounter } from 'routes/shop/bag/components/BagCounter/BagCounter';

type UseNavigationParams = {
  styles?: {
    overlay?: string;
    overlayOpen?: string;
    overlayClosed?: string;
    menuButton?: string;
    bagButton?: string;
    closeButton?: string;
    menuContainer?: string;
    menuContainerOpen?: string;
    menuContainerClosed?: string;
    bagContainer?: string;
  };
};

export const useNavigation = ({ styles }: UseNavigationParams = {}) => {
  const { packages } = useAppContext();
  const { pathname, push } = useRouter();
  const menuButtonRef = React.useRef<HTMLButtonElement>(null);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const floatingNavRef = React.useRef(null);
  const { ref: navigationRef } = useFocusTrap(isMenuOpen);
  const { isFloating } = useFloatingNav(floatingNavRef);

  const { rootUrl } = useConfig();
  const { slug, showPortfolio, showTeam } = useWebsite();
  const { isMultiUser, name: businessName, socialMediaUrls } = useBusiness();

  const { isEnabled: showShopLink } = useFeatureFlag(FEATURE_FLAGS.NILE_PILOT);
  const { isEnabled: isPackagePurchaseEnabled } = useFeatureFlag(
    FEATURE_FLAGS.PACKAGE_PURCHASE,
  );

  // Menu Toggle Functionality
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  const handleBagClick = () => void push('/shop/bag');
  const closeMenu = () => {
    setIsMenuOpen(false);
    menuButtonRef.current?.focus();
  };

  React.useEffect(() => {
    const documentKeyDownHandler = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        closeMenu();
      }
    };

    if (isMenuOpen) {
      document.addEventListener('keydown', documentKeyDownHandler);
      document.body.style.overflow = 'hidden';
    } else {
      document.removeEventListener('keydown', documentKeyDownHandler);
      document.body.style.overflow = 'auto';
    }

    return () =>
      document.removeEventListener('keydown', documentKeyDownHandler);
  }, [isMenuOpen]);

  const getPackagesUrl = () => {
    if (!packages?.length) return null;
    return isPackagePurchaseEnabled ? '/shop/packages' : '/packages';
  };

  // Links
  const termsOfUseUrl = `${rootUrl}terms`;
  const privacyPolicyUrl = `${rootUrl}privacy`;
  const shopUrl = `https://glossgenius.myshopify.com/collections/${slug}`;

  const isHome = pathname === '/';
  const aboutUrl = '/about';
  const teamUrl = '/team';
  const servicesUrl = '/services';
  const portfolioUrl = '/portfolio';
  const contactUrl = '/contact';
  const bookUrl = '/book';
  const bagUrl = '/shop/bag';
  const packagesUrl = getPackagesUrl();

  // Render functions
  const renderOverlay = () => {
    return (
      <div
        data-testid="nav-overlay"
        className={cx(
          styles?.overlay,
          isMenuOpen ? styles?.overlayOpen : styles?.overlayClosed,
        )}
        onClick={toggleMenu}
        aria-hidden="true"
      />
    );
  };

  const trackNavItemClicked = (
    page:
      | 'about'
      | 'team'
      | 'services'
      | 'portfolio'
      | 'contact'
      | 'book'
      | 'shop'
      | 'packages',
  ) => {
    trackEvent(EventNames.CLIENT_NAVIGATED_TO_PAGE, {
      page_name: page,
    });
  };

  const renderMenuButton = ({ content }: { content: React.ReactNode }) => {
    return (
      <button
        ref={menuButtonRef}
        title={`Navigation Menu${
          isMenuOpen ? ' - Press Escape to close the menu' : ''
        }`}
        className={styles?.menuButton}
        type="button"
        onClick={toggleMenu}
        aria-expanded={isMenuOpen}
      >
        {content}
      </button>
    );
  };

  const renderBagButton = (toggledStyle: string) => {
    return isPackagePurchaseEnabled ? (
      <Button
        appearance="transparent"
        className={cx(styles?.bagButton, toggledStyle)}
        onClick={handleBagClick}
        title={`Bag preview`}
      >
        <BagCounter size={28} />
      </Button>
    ) : null;
  };

  const renderCloseButton = ({ content }: { content: React.ReactNode }) => {
    return (
      <button
        title="Close the navigation menu"
        className={styles?.closeButton}
        type="button"
        onClick={closeMenu}
      >
        {content}
      </button>
    );
  };

  const renderMenuContainer = React.useCallback(
    ({ content, isOpen }: { content: React.ReactNode; isOpen: boolean }) => (
      <div
        className={cx(
          styles?.menuContainer,
          isOpen ? styles?.menuContainerOpen : styles?.menuContainerClosed,
        )}
      >
        {content}
      </div>
    ),
    [
      styles?.menuContainer,
      styles?.menuContainerClosed,
      styles?.menuContainerOpen,
    ],
  );

  return {
    showPortfolio,
    showTeam: showTeam && isMultiUser,
    showShopLink,
    isMenuOpen,
    isHome,
    businessName,
    bagUrl,
    aboutUrl,
    teamUrl,
    servicesUrl,
    portfolioUrl,
    contactUrl,
    bookUrl,
    packagesUrl,
    termsOfUseUrl,
    privacyPolicyUrl,
    shopUrl,
    isPackagePurchaseEnabled,
    tracking: {
      navItemClicked: trackNavItemClicked,
    },
    renderOverlay,
    renderBagButton,
    renderMenuButton,
    renderCloseButton,
    renderMenuContainer,
    isFloating,
    floatingNavRef,
    navigationRef,
    socialMediaUrls,
  };
};
